import {
  CarrierType,
  CarrierVisitDto,
  CarrierVisitStatus,
  CarrierVisitsApi,
  EquipmentDto,
  EquipmentType,
} from '@operations/app/api'
import { CarrierVisitRefreshEventPayload } from '@operations/app/api/signalRDtos/carrierVisitRefreshEventPayload'
import { createApiClient } from '@operations/app/http-client'
import { EventTypes } from '@operations/messages/eventsTypes'
import { equipmentService } from '@operations/services'
import { AppStore, IEvent, IMessageBus, PATHS } from '@tom-ui/utils'
import { action, makeObservable, observable, runInAction } from 'mobx'

export class CraneOperatorLandingContainerUIStore {
  loadingSignalR = false

  vesselVisitsWithGeometry: CarrierVisitDto[] = []
  plannedCranesOfSelectedVisit: EquipmentDto[] = []

  selectedVesselVisitId?: number
  selectedEquipmentId?: number

  constructor(
    private messageBus: IMessageBus,
    private appStore: AppStore,
  ) {
    makeObservable(this, {
      vesselVisitsWithGeometry: observable,
      plannedCranesOfSelectedVisit: observable,

      selectedVesselVisitId: observable,
      selectedEquipmentId: observable,

      setSelectedVesselVisitId: action,
      setSelectedEquipmentId: action,
    })

    this.messageBus.subscribeEvent(
      EventTypes.CarrierVisitRefresh,
      this.reloadVisitsAndCranesBySignalR,
    )

    this.messageBus.subscribeEvent<number>(
      EventTypes.CraneSplitGeneratedForVisit,
      this.handleCraneSplitGeneratedForVisitEvent,
    )
  }

  setSelectedVesselVisitId(id?: number): void {
    if (this.selectedVesselVisitId != id) {
      this.selectedVesselVisitId = id
      this.setSelectedEquipmentId()
      this.loadPlannedCranesOfSelectedVisit()
    }
  }

  setSelectedEquipmentId(id?: number): void {
    if (this.selectedEquipmentId != id) this.selectedEquipmentId = id
  }

  async loadPlannedCranesOfSelectedVisit() {
    const data = await equipmentService.load({
      vesselVisitId: this.selectedVesselVisitId,
      equipmentType: EquipmentType.Sts,
      includeInactive: true,
    })

    runInAction(() => (this.plannedCranesOfSelectedVisit = data))
  }

  async loadNotLeftTerminalVesselVisits() {
    const { data } = await createApiClient(CarrierVisitsApi).get(CarrierType.Vessel, undefined, [
      CarrierVisitStatus.InOperation,
      CarrierVisitStatus.Arrived,
      CarrierVisitStatus.Expected,
    ])
    runInAction(() => (this.vesselVisitsWithGeometry = data))
  }

  reloadVisitsAndCranesBySignalR = async (res: IEvent<CarrierVisitRefreshEventPayload>) => {
    //refresh only if event update is about everything(carrierVisits is null) or about vessel visits
    if (
      res.payload?.carrierVisits?.length &&
      !res.payload?.carrierVisits.find(c => c.carrierType === CarrierType.Vessel)
    )
      return

    this.reloadVisitsAndCranes()
  }

  private readonly handleCraneSplitGeneratedForVisitEvent = async (event: IEvent<number>) => {
    const visitId = event.payload

    if (this.selectedVesselVisitId === visitId) {
      await this.reloadVisitsAndCranes()
    }
  }

  async reloadVisitsAndCranes() {
    if (!this.loadingSignalR) {
      this.loadingSignalR = true
      await this.appStore.triggerReloadBySignalR(async () => {
        await Promise.all([
          this.loadNotLeftTerminalVesselVisits(),
          this.loadPlannedCranesOfSelectedVisit(),
        ])
      }, PATHS.operators.crane.url)
      this.loadingSignalR = false
    }
  }
}
